import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { Check, ChevronDown, Search, X } from 'lucide-react';

export interface FilterSelectProps<T> {
  /** Filter label shown when nothing is selected (e.g. "Status"). */
  label: string;
  /** All available options. */
  items: T[];
  /** Selected ids (string for single, string[] for multiple). */
  value: string | string[] | null;
  /** Called with the next value. String for single, string[] for multiple. */
  onChange: (value: string | string[]) => void;
  /** Extract the unique id used for `value`. */
  getValue: (item: T) => string;
  /** Extract the display label. */
  getLabel: (item: T) => string;
  /** Optional row decoration (color swatch, icon). */
  renderItem?: (item: T) => ReactNode;
  /** Multi-select renders checkboxes; single renders radios. */
  multiple?: boolean;
  /** Show a search input at the top of the dropdown. */
  searchable?: boolean;
  /** Override the default substring filter (case-insensitive on label). */
  filterFn?: (item: T, query: string) => boolean;
  disabled?: boolean;
  /** Empty state when items is empty. */
  emptyMessage?: string;
  /** Message shown when search has no match. */
  noMatchMessage?: string;
  /** Custom search placeholder. */
  searchPlaceholder?: string;
  /** Optional className on the outer wrapper. */
  className?: string;
}

function defaultFilter<T>(item: T, query: string, getLabel: (item: T) => string): boolean {
  return getLabel(item).toLowerCase().includes(query.toLowerCase());
}

/**
 * Compact filter control: a pill button that opens a popover with
 * checkboxes (multi) or radios (single). Designed for filter bars where
 * vertical space is tight — selections aren't shown inline.
 */
export function FilterSelect<T>({
  label,
  items,
  value,
  onChange,
  getValue,
  getLabel,
  renderItem,
  multiple = false,
  searchable = true,
  filterFn,
  disabled = false,
  emptyMessage = 'No options',
  noMatchMessage = 'No results',
  searchPlaceholder = 'Search...',
  className,
}: FilterSelectProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // ─── Selected ids ─────────────────
  const selectedIds = useMemo(() => {
    if (multiple) return Array.isArray(value) ? value : [];
    if (typeof value === 'string' && value) return [value];
    return [];
  }, [multiple, value]);

  const itemsById = useMemo(() => {
    const map = new Map<string, T>();
    for (const item of items) map.set(getValue(item), item);
    return map;
  }, [items, getValue]);

  const hasValue = selectedIds.length > 0;

  // ─── Filter ──────────────────────
  const filtered = useMemo(() => {
    const q = query.trim();
    if (!q) return items;
    const fn = filterFn ?? ((item: T) => defaultFilter(item, q, getLabel));
    return items.filter((item) => fn(item, q));
  }, [items, query, filterFn, getLabel]);

  // ─── Button label ────────────────
  const buttonLabel = useMemo(() => {
    if (selectedIds.length === 0) return label;
    if (multiple) {
      if (selectedIds.length === 1) {
        const item = itemsById.get(selectedIds[0]);
        return item ? getLabel(item) : label;
      }
      return `${label} (${selectedIds.length})`;
    }
    const item = itemsById.get(selectedIds[0]);
    return item ? getLabel(item) : label;
  }, [selectedIds, multiple, label, itemsById, getLabel]);

  // ─── Open / close ─────────────────
  const open = useCallback(() => {
    if (disabled) return;
    setIsOpen(true);
    setQuery('');
  }, [disabled]);

  const close = useCallback(() => {
    setIsOpen(false);
    setQuery('');
  }, []);

  const toggle = useCallback(() => {
    if (isOpen) close();
    else open();
  }, [isOpen, open, close]);

  // Close on outside click.
  useEffect(() => {
    if (!isOpen) return;
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        close();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, close]);

  // Focus search when opened.
  useEffect(() => {
    if (isOpen && searchable && searchRef.current) {
      searchRef.current.focus();
    }
  }, [isOpen, searchable]);

  // ─── Item toggle ──────────────────
  const toggleItem = useCallback(
    (item: T) => {
      const id = getValue(item);
      if (multiple) {
        const current = Array.isArray(value) ? value : [];
        if (current.includes(id)) {
          onChange(current.filter((v) => v !== id));
        } else {
          onChange([...current, id]);
        }
      } else {
        onChange(id);
        close();
      }
    },
    [getValue, multiple, value, onChange, close],
  );

  const handleClear = useCallback(
    (e?: React.MouseEvent) => {
      e?.stopPropagation();
      onChange(multiple ? [] : '');
    },
    [multiple, onChange],
  );

  const handleButtonKeyDown = useCallback(
    (e: KeyboardEvent<HTMLButtonElement>) => {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        open();
      }
    },
    [open],
  );

  const handleSearchKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
        buttonRef.current?.focus();
      }
    },
    [close],
  );

  return (
    <div ref={containerRef} className={`relative inline-block ${className ?? ''}`}>
      {/* Pill button */}
      <button
        ref={buttonRef}
        type="button"
        onClick={toggle}
        onKeyDown={handleButtonKeyDown}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={hasValue ? `${label}: ${buttonLabel}` : label}
        className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full border text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
          hasValue
            ? 'border-primary-300 bg-primary-50 text-primary-700 hover:bg-primary-100'
            : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
        }`}
      >
        <span>{buttonLabel}</span>
        {hasValue && !disabled ? (
          <span
            role="button"
            tabIndex={-1}
            aria-label={`Clear ${label}`}
            onClick={handleClear}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                handleClear();
              }
            }}
            className="text-primary-500 hover:text-primary-700 cursor-pointer"
          >
            <X size={14} />
          </span>
        ) : (
          <ChevronDown
            size={14}
            className={`text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        )}
      </button>

      {/* Popover */}
      {isOpen && (
        <div
          role="dialog"
          aria-label={label}
          className="absolute z-20 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden"
        >
          {searchable && items.length > 0 && (
            <div className="p-2 border-b border-gray-100">
              <div className="relative">
                <Search
                  size={14}
                  className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                />
                <input
                  ref={searchRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  placeholder={searchPlaceholder}
                  aria-label={`Search ${label}`}
                  className="w-full pl-7 pr-2 py-1.5 border border-gray-200 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
          )}

          <ul
            role="listbox"
            aria-multiselectable={multiple}
            className="max-h-60 overflow-auto py-1"
          >
            {items.length === 0 && (
              <li className="px-3 py-2 text-xs text-gray-500" role="status">
                {emptyMessage}
              </li>
            )}
            {items.length > 0 && filtered.length === 0 && (
              <li className="px-3 py-2 text-xs text-gray-500" role="status">
                {noMatchMessage}
              </li>
            )}
            {filtered.map((item) => {
              const id = getValue(item);
              const isSelected = selectedIds.includes(id);
              return (
                <li
                  key={id}
                  role="option"
                  aria-selected={isSelected}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    toggleItem(item);
                  }}
                  className={`flex items-center gap-2 px-3 py-1.5 cursor-pointer text-sm ${
                    isSelected ? 'bg-primary-50' : 'hover:bg-gray-50'
                  }`}
                >
                  <span
                    aria-hidden="true"
                    className={`flex-shrink-0 w-4 h-4 flex items-center justify-center border ${multiple ? 'rounded' : 'rounded-full'} ${
                      isSelected ? 'bg-primary-600 border-primary-600' : 'bg-white border-gray-300'
                    }`}
                  >
                    {isSelected &&
                      (multiple ? (
                        <Check size={12} className="text-white" />
                      ) : (
                        <span className="w-1.5 h-1.5 rounded-full bg-white" />
                      ))}
                  </span>
                  <span className="flex-1 min-w-0 text-gray-900">
                    {renderItem ? renderItem(item) : getLabel(item)}
                  </span>
                </li>
              );
            })}
          </ul>

          {hasValue && (
            <div className="border-t border-gray-100 p-2 flex justify-between items-center">
              <button
                type="button"
                onClick={() => handleClear()}
                className="text-xs text-primary-600 hover:text-primary-700 font-medium"
              >
                Clear
              </button>
              <button
                type="button"
                onClick={close}
                className="text-xs text-gray-500 hover:text-gray-700"
              >
                Done
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
